import { Link, useLocation } from 'react-router-dom'
import { Home, Video, MoreHorizontal, LucideIcon } from 'lucide-react'

export interface NavItem {
  icon: LucideIcon
  label: string
  href: string
  id: string
}

export interface BottomNavigationProps {
  onMoreClick: () => void
  navItems?: NavItem[]
  basePath?: string
}

const defaultUserNavItems: NavItem[] = [
  {
    icon: Home,
    label: 'Dashboard',
    href: '/u/dashboard',
    id: 'dashboard'
  },
  {
    icon: Video,
    label: 'Programme',
    href: '/u/program',
    id: 'program'
  }
]

export function BottomNavigation({
  onMoreClick,
  navItems = defaultUserNavItems,
  basePath = '/u'
}: BottomNavigationProps) {
  const location = useLocation()
  const pathname = location.pathname

  const isActive = (href: string) => {
    // Exact match for dashboard
    if (href === `${basePath}/dashboard`) {
      return pathname === `${basePath}/dashboard`
    }
    // Prefix match for other routes
    return pathname?.startsWith(href)
  }

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E3DED2] z-40"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Mobile navigation"
    >
      <div className="flex items-center justify-around h-16">
        {navItems.map((item) => {
          const Icon = item.icon
          const active = isActive(item.href)

          return (
            <Link
              key={item.id}
              to={item.href}
              aria-current={active ? 'page' : undefined}
              className={`
                flex flex-col items-center justify-center gap-1 min-w-[44px] min-h-[44px] flex-1
                transition-colors
                ${active
                  ? 'text-[#0F1C2E]'
                  : 'text-[#6E7582] hover:text-[#0F1C2E]'
                }
              `}
            >
              <Icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : 'stroke-2'}`} />
              <span className={`text-xs ${active ? 'font-semibold' : 'font-medium'}`}>
                {item.label}
              </span>
            </Link>
          )
        })}

        {/* More Button */}
        <button
          onClick={onMoreClick}
          aria-label="More options"
          className="flex flex-col items-center justify-center gap-1 min-w-[44px] min-h-[44px] flex-1 text-[#6E7582] hover:text-[#0F1C2E] transition-colors"
        >
          <MoreHorizontal className="w-5 h-5 stroke-2" />
          <span className="text-xs font-medium">More</span>
        </button>
      </div>
    </nav>
  )
}
